import React, { useState, useRef, useEffect } from 'react';
import {cloneDeep} from "lodash";
import InvisiblePane from "../../Components/InvisiblePane";
import Text from "../../Components/Text";
import ProgressBar from "../../Components/ProgressBar";
import Pane from "../../Components/Pane";
import BackButton from "../../Components/BackButton";
import Button from "../../Components/Button";
import TransferringIsaDetails from "./Components/TransferringIsaDetails";
import PrimaryTitle from "../../Components/PrimaryTitle";
import {BOOLEAN_OPTIONS, DEFAULT_ERROR_MESSAGE, OPENING, OPENING_OR_TRANSFERRING, TRANSFERRING} from "../../constants";
import Error from "../../Components/Error";
import PageLoadingPanel from "../../Components/PageLoadingPanel";
import FormInput from "../../Components/Forms/FormInput";
import OpeningIsaDetails from "./Components/OpeningIsaDetails";

const DEFAULT_OPENING_ISA_DETAILS = {
    'isa_type':     '',
    'isa_amount':   '',
}

const DEFAULT_TRANSFER_ISA_DETAILS = {
    'isa_type':                                      '',
    'isa_amount':                                    '',
    'current_isa_reference_number':                  '',
    'current_isa_manager':                           '',
    'current_isa_manager_address':                   '',
    'current_isa_transfer_date_requested':           '',
    'current_isa_first_subscription_current_year':   '',
}

const IsaDetailsPage = (props) => {
    const {
        previousPage    = () => {},
        nextPage        = () => {}
    } = props;

    const PAGE_TITLE            = 'ISA Details';
    const CURRENT_STEP_NUMBER   = 3;
    const TOTAL_STEP_NUMBER     = 8;

    const [data, setData] = useState({
        'first_isa_option_selected':    '',
        'second_isa_option_selected':   0,
        'opening_isa_detail':           cloneDeep(DEFAULT_OPENING_ISA_DETAILS),
        'transferring_isa_detail':      cloneDeep(DEFAULT_TRANSFER_ISA_DETAILS),
    });
    const [loaded, setLoaded]               = useState(false);
    const [errors, setErrors]               = useState({});
    const [errorMessage, setErrorMessage]   = useState(null);
    const [submitting, setSubmitting]       = useState(false);

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const load = () => {
        axios.get(route('web-api.session.isa-details.get', {session: window.session}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    if (response.data.opening_isa_detail !== null && response.data.transferring_isa_detail !== null) {
                        response.data.second_isa_option_selected = 1;
                    } else {
                        response.data.second_isa_option_selected = 0;
                    }
                    setData(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoaded(true);
            });
    }

    const submit = () => {
        setErrorMessage(null);
        let clone = cloneDeep(data);
        clone.second_isa_option_selected = parseInt(clone.second_isa_option_selected);

        console.log(clone);

        axios.patch(route('web-api.session.isa-details.update', {session: window.session}).toString(), clone)
            .then((response) => {
                setSubmitting(false);
                nextPage();
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        setErrorMessage(error.response.data.message);
                    }

                    if (!!error.response.data.errors) {
                        setErrors(error.response.data.errors);
                    }

                    setSubmitting(false);
                } else {
                    console.log(error);
                    setErrorMessage(DEFAULT_ERROR_MESSAGE);
                }
            });
    }

    const setFirstIsaOption = (value) => {
        let clone = cloneDeep(data);
        clone.first_isa_option_selected = value;
        clone.second_isa_option_selected = '';

        switch (value){
            case OPENING:
                clone.opening_isa_detail = cloneDeep(DEFAULT_OPENING_ISA_DETAILS);
                clone.transferring_isa_detail = null;
                break;
            case TRANSFERRING:
                clone.transferring_isa_detail = cloneDeep(DEFAULT_TRANSFER_ISA_DETAILS);
                clone.opening_isa_detail = null;
                break;
        }

        setData(clone);
        setErrors({});
        setErrorMessage(null);
    }

    const addSecondIsaOption = (value) => {
        let clone = cloneDeep(data);
        clone.second_isa_option_selected = value;

        switch (clone.first_isa_option_selected){
            case OPENING:
                clone.transferring_isa_detail = cloneDeep(DEFAULT_TRANSFER_ISA_DETAILS);
                break;
            case TRANSFERRING:
                clone.opening_isa_detail = cloneDeep(DEFAULT_OPENING_ISA_DETAILS);
                break;
        }

        setData(clone);
    }

    const updateField = (type, field, value) => {
        let clone = cloneDeep(data);

        switch (type) {
            case 'transferring':
                clone.transferring_isa_detail[field] = value;
                break;
            case 'opening':
                clone.opening_isa_detail[field] = value;
                break;
            default:
                clone[field] = value;
                break;
        }

        setData(clone);
    }

    const getSubfieldErrors = (field) => {
        const subfieldErrors = {};

        for (const key in errors) {
            if (key.startsWith(field)) {
                const subfieldKey = key.replace(`${field}.`, '');
                subfieldErrors[subfieldKey] = errors[key];
            }
        }

        return subfieldErrors;
    };

    const renderTopText = () => {
        return (
            <InvisiblePane style={{paddingTop: 0}}>
                <Text style={{marginBottom: '0', textAlign: 'center', fontWeight: 'bold'}}>Please check the business details - the better we understand your business, the easier it is for us to help you</Text>
            </InvisiblePane>
        );
    }

    const renderFirstSection = () => {
        if (data.first_isa_option_selected === '') {
            return null;
        }

        if (data.first_isa_option_selected === OPENING) {
            return (
                <OpeningIsaDetails
                    data={data.opening_isa_detail}
                    onChange={(field, value) => updateField('opening', field, value)}
                    disabled={submitting}
                    errors={getSubfieldErrors('opening_isa_detail')}
                />
            );
        }

        return (
            <TransferringIsaDetails
                data={data.transferring_isa_detail}
                onChange={(field, value) => updateField('transferring', field, value)}
                disabled={submitting}
                errors={getSubfieldErrors('transferring_isa_detail')}
            />
        );
    }

    const renderSecondSection = () => {
        if (data.first_isa_option_selected === '' || data.second_isa_option_selected === '0' || data.opening_isa_detail === null || data.transferring_isa_detail === null) {
            return null;
        }

        if (data.second_isa_option_selected === '1' || data.second_isa_option_selected === 1) {
            if (data.first_isa_option_selected === TRANSFERRING) {
                return (
                    <OpeningIsaDetails
                        data={data.opening_isa_detail}
                        onChange={(field, value) => updateField('opening', field, value)}
                        disabled={submitting}
                        errors={getSubfieldErrors('opening_isa_detail')}
                    />
                );
            }

            return (
                <TransferringIsaDetails
                    data={data.transferring_isa_detail}
                    onChange={(field, value) => updateField('transferring', field, value)}
                    disabled={submitting}
                    errors={getSubfieldErrors('transferring_isa_detail')}
                />
            );
        }

        return null;
    }

    if (!loaded) {
        return (
            <PageLoadingPanel
                pageTitle={PAGE_TITLE}
                currentStepNumber={CURRENT_STEP_NUMBER}
                totalStepNumber={TOTAL_STEP_NUMBER}
                upperTextComponent={renderTopText()}
            />
        )
    }

    return (
        <React.Fragment>
            {renderTopText()}
            {!!errorMessage && (
                <Error message={errorMessage} />
            )}
            <Pane>
                <ProgressBar
                    title={PAGE_TITLE}
                    currentStepNumber={CURRENT_STEP_NUMBER}
                    totalStepNumber={TOTAL_STEP_NUMBER}
                />
                <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                <FormInput
                    required={true}
                    name={'first_isa_option_selected'}
                    detailedLabel={'Are you opening or transferring cash ISA'}
                    value={data.first_isa_option_selected}
                    type={'select'}
                    options={OPENING_OR_TRANSFERRING}
                    onChange={(value) => setFirstIsaOption(value)}
                    disabled={submitting}
                    error={errors}
                />
                {renderFirstSection()}

                {data.first_isa_option_selected !== '' && (
                    <FormInput
                        required={true}
                        name={'second_isa_option_selected'}
                        detailedLabel={data.first_isa_option_selected === OPENING ? 'Do you want to transfer a cash ISA too?' : 'Do you want to open a new cash ISA too?'}
                        value={data.second_isa_option_selected}
                        type={'select'}
                        options={BOOLEAN_OPTIONS}
                        onChange={(value) => addSecondIsaOption(value)}
                        disabled={submitting}
                        error={errors}
                    />
                )}
                {renderSecondSection()}
            </Pane>
            {!!errorMessage && (
                <Error message={errorMessage} />
            )}
            <InvisiblePane style={{paddingTop: '59px', paddingLeft: 0, paddingRight: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <BackButton
                    disabled={submitting}
                    onClick={() => previousPage()}
                />
                <Button
                    label={submitting ? 'Submitting...' : 'Next'}
                    disabled={submitting}
                    onClick={() => setSubmitting(true)}
                />
            </InvisiblePane>
        </React.Fragment>
    );
}

export default IsaDetailsPage;
